import { useState, useCallback } from "react";
import {
    getStorage,
    ref as storageRef,
    uploadBytes,
    getDownloadURL,
    deleteObject,
} from "firebase/storage";

/**
 * Custom hook encapsulating profile image selection, validation, upload, and cleanup.
 *
 * @param {object} params
 * @param {object} params.currentUser - Firebase auth user.
 * @param {object} params.profile - User profile data (needs .photoURL).
 * @param {function} params.updateUserProfile - Function to persist photoURL to profile.
 * @param {string} params.language - "en" | "zh".
 */
export function useImageUpload({
    currentUser,
    profile,
    updateUserProfile,
    language,
}) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewURL, setPreviewURL] = useState("");
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const handleFileSelect = useCallback(
        (e) => {
            const file = e.target.files[0];

            if (!file) {
                setSelectedFile(null);
                setPreviewURL("");
                return;
            }

            const validTypes = ["image/jpeg", "image/png", "image/jpg"];
            if (!validTypes.includes(file.type)) {
                setError(
                    language === "zh"
                        ? "無效的檔案類型。請選擇 JPEG 或 PNG 圖片。"
                        : "Invalid file type. Please select a JPEG or PNG image.",
                );
                setSelectedFile(null);
                setPreviewURL("");
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                setError(
                    language === "zh"
                        ? "檔案大小超過 5MB 限制。"
                        : "File size exceeds 5MB limit.",
                );
                setSelectedFile(null);
                setPreviewURL("");
                return;
            }

            setSelectedFile(file);
            setError("");

            const reader = new FileReader();
            reader.onload = (event) => setPreviewURL(event.target.result);
            reader.readAsDataURL(file);
        },
        [language],
    );

    const handleImageUpload = useCallback(async () => {
        if (!selectedFile) {
            alert(
                language === "zh"
                    ? "請選擇要上傳的圖片檔案。"
                    : "Please select an image file to upload.",
            );
            return null;
        }

        setUploading(true);
        setError("");
        setSuccess("");

        try {
            const oldPhotoURL = profile?.photoURL;

            // Upload to Firebase Storage
            const storage = getStorage();
            const imageRef = storageRef(
                storage,
                `profile-images/${currentUser.uid}/${Date.now()}_${selectedFile.name}`,
            );
            const snapshot = await uploadBytes(imageRef, selectedFile);
            const downloadURL = await getDownloadURL(snapshot.ref);

            // Update profile
            await updateUserProfile({ photoURL: downloadURL });

            // Delete old image if it's from our storage
            if (oldPhotoURL && oldPhotoURL.includes("firebase")) {
                try {
                    const storage = getStorage();
                    const oldImageRef = storageRef(storage, oldPhotoURL);
                    await deleteObject(oldImageRef);
                } catch {
                    // Ignore deletion errors — upload was successful
                }
            }

            // Reset state
            setSelectedFile(null);
            setPreviewURL("");
            setSuccess(
                language === "zh"
                    ? "頭像上傳成功！"
                    : "Profile picture uploaded successfully!",
            );
            setTimeout(() => setSuccess(""), 3000);

            return downloadURL;
        } catch (err) {
            console.error("Upload error:", err);
            setError(
                language === "zh"
                    ? `上傳失敗：${err.message}`
                    : `Upload failed: ${err.message}`,
            );
            return null;
        } finally {
            setUploading(false);
        }
    }, [selectedFile, currentUser, profile, updateUserProfile, language]);

    return {
        selectedFile,
        previewURL,
        uploading,
        error,
        success,
        handleFileSelect,
        handleImageUpload,
    };
}
